import { getData } from "@/lib/utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isUndefined } from "lodash";

// Define the Product interface
interface Product {
  quatationId: any;
  productId: string;
  unitName: string;
  SKUCode: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  image?: string | null;
  company: {
    CompanyNr: string;
    id: string;
    nameAr: string;
  };
  tender: { title: string; id: string; status: string };
}

// Define the AuthState interface
export interface AuthState {
  authUser: any | null; // Replace 'any' with your specific user type if available
  loadUser: boolean;
  categories: any[];
  myCompany: any | null;
  cart: Product[]; // Add cart to the state
}

// Define the initial state
const initialState: AuthState = {
  authUser: isUndefined(getData("userInfo")) ? null : getData("userInfo"),
  myCompany: null,
  categories: [],
  loadUser: false,
  cart: [], // Initialize cart as an empty array
};

// Create the slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateAuthUser(state, action: PayloadAction<any>) {
      state.authUser = action.payload;
      state.loadUser = true;
    },
    updateLoadUser(state, action: PayloadAction<boolean>) {
      state.loadUser = action.payload;
    },
    getMyCompany(state, action: PayloadAction<any>) {
      state.myCompany = action.payload;
    },
    getCategories(state, action: PayloadAction<any>) {
      state.categories = action.payload;
    },
    addToCart(state, action: PayloadAction<Product>) {
      // Check if the product already exists in the cart
      const existingProductIndex = state.cart.findIndex(
        (item) => item.productId === action.payload.productId
      );

      if (existingProductIndex >= 0) {
        // If it exists, update the quantity
        state.cart[existingProductIndex].quantity += action.payload.quantity;
      } else {
        // If it doesn't exist, add it to the cart
        state.cart.push(action.payload);
      }
    },
    updateCartItemQuantity(
      state,
      action: PayloadAction<{ id: string; quantity: number }>
    ) {
      const item = state.cart.find(
        (item) => item.productId === action.payload.id
      );
      if (item && action.payload.quantity > 0) {
        item.quantity = action.payload.quantity;
      }
    },
    removeFromCart(state, action: PayloadAction<string>) {
      // Remove the product by ID
      state.cart = state.cart.filter(
        (item) => item.productId !== action.payload
      );
    },
    EmptyCart(state) {
      // Remove the product by ID
      state.cart = [];
    },
  },
});

// Export actions
export const {
  updateAuthUser,
  updateLoadUser,
  getMyCompany,
  getCategories,
  addToCart,
  EmptyCart,
  removeFromCart,
  updateCartItemQuantity,
} = authSlice.actions;

// Export the reducer and the AuthState type
export default authSlice.reducer;
